import React, { useState, useMemo, useEffect, useRef } from "react";
// @ts-ignore
import OrgChart from "@dabeng/react-orgchart";

import { InboxOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { Spin, Tooltip } from "antd";
import styles from "../OrganisationChart/OrganisationChart.module.scss";
import { getOrganisationChartData } from "./SideBarOrganisationChartHelper";
import { getAllDescendantIds, getSiblingIds, buildTree, EmployeeNode, centerChart } from "../OrganisationChart/OrganisationChartHelper";

const EmployeeOrganizationChartTab: React.FC = () => {
  const orgId = useSelector((state: any) => state?.organisationId?.id);
  const [orgData, setOrgData] = useState<EmployeeNode[]>([]);
  const [rootName, setRootName] = useState<string>("Root");
  const [rootDesignation, setRootDesignation] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set([0]));

  const chartWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!loading && orgData.length > 0) {
      centerChart(chartWrapperRef);
    }
  }, [loading, orgData]);

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);

      const minDelay = new Promise((resolve) => setTimeout(resolve, 1000));
      const fetchPromise = (async () => {
        try {
          const res = await getOrganisationChartData(orgId);
          const apiData = res?.data;

          if (apiData?.ceoName && Array.isArray(apiData.subordinates)) {
            const ceoFullName = apiData.ceoName;
            const match = ceoFullName.match(/^(.+?)\s*\((.+)\)$/);
            const rootName = match ? match[1].trim() : ceoFullName;
            const rootDesignation = match ? match[2].trim() : "";

            setRootName(rootName);
            setRootDesignation(rootDesignation);
            setOrgData(apiData.subordinates);
            setExpandedNodes(new Set([0]));
          } else {
            setRootName("Root");
            setRootDesignation("");
            setOrgData([]);
          }
        } catch (err) {
          console.error("Error fetching org chart data:", err);
          setOrgData([]);
        }
      })();

      await Promise.all([fetchPromise, minDelay]);
      setLoading(false);
    };

    fetchData();
  }, [orgId]);

  const toggleExpand = (nodeId: number, parentNode: EmployeeNode | null = null) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
        const findNode = (nodes: EmployeeNode[]): EmployeeNode | null => {
          for (let n of nodes) {
            if (n.employeeId === nodeId) return n;
            const found = findNode(n.subordinates);
            if (found) return found;
          }
          return null;
        };
        const node = nodeId === 0 ? ({ subordinates: orgData } as EmployeeNode) : findNode(orgData);
        if (node) {
          getAllDescendantIds(node).forEach((id) => newSet.delete(id));
        }
      } else {
        const siblings = nodeId === 0 ? [] : getSiblingIds(orgData, parentNode, nodeId);
        siblings.forEach((id) => newSet.delete(id));
        newSet.add(nodeId);
      }

      if (nodeId === 0) {
        setTimeout(() => centerChart(chartWrapperRef), 300);
      }

      return newSet;
    });
  };

  const datasource = useMemo(
    () => ({
      name: rootName,
      title: rootDesignation,
      employeeId: 0,
      originalData: null,
      children: expandedNodes.has(0) ? orgData.map((emp) => buildTree(emp, expandedNodes)) : [],
    }),
    [orgData, expandedNodes, rootName, rootDesignation],
  );

  const NodeTemplate: React.FC<{ nodeData: any }> = ({ nodeData }) => {
    const hasChildren = nodeData.originalData?.subordinates?.length > 0 || nodeData.employeeId === 0;
    return (
      <div
        className="p-2 m-1 border-2 rounded-lg text-white text-center font-bold cursor-pointer relative bg-[#d43f8d] border-[#ff9ccf]"
        onClick={() => hasChildren && toggleExpand(nodeData.employeeId, nodeData.parentNode)}
      >
        {nodeData.name.length > 30 ? (
          <Tooltip title={nodeData.name} mouseEnterDelay={0.3}>
            <div className="font-rubik text-[12px] truncate">{nodeData.name}</div>
          </Tooltip>
        ) : (
          <div className="font-rubik text-[12px] truncate">{nodeData.name}</div>
        )}
        {nodeData.title && <div className="whitespace-normal break-words font-normal text-[10px] mt-1 font-rubik">{nodeData.title}</div>}
      </div>
    );
  };

  return (
    <div className="w-full min-h-[70vh] bg-white relative border-gray-200">
      {loading && (
        <div className={styles.loaderOverlay}>
          <Spin />
        </div>
      )}

      {!loading && (!orgData || orgData.length === 0) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 font-rubik">
          <InboxOutlined className="text-[60px] w-[60px] h-[60px] mb-4" />
          <div className="text-sm font-normal text-rubik">No Data</div>
        </div>
      )}

      {!loading && orgData && orgData.length > 0 && (
        <div ref={chartWrapperRef} className="w-full h-full overflow-auto flex justify-center">
          <OrgChart datasource={datasource} NodeTemplate={NodeTemplate} chartClass="org-chart" draggable={false} />
        </div>
      )}
    </div>
  );
};

export default EmployeeOrganizationChartTab;
